// =====================================================================
// Humanização de rótulos — fonte ÚNICA de tradução técnico→pt-BR usada por
// API (resumo do agente) e SPA (tabelas, KPIs, Central de inconsistências).
// Sem dependência de UI: severidade carrega um "tone" semântico; quem
// renderiza mapeia tone→cor. Evita que identificadores de banco
// (snake_case, códigos de enum, JSON cru) vazem para o usuário.
// =====================================================================
import { ENTITIES } from "./catalog.js";
import { ROLES } from "./types.js";

// 1) Base: rótulos já definidos no catálogo de entidades (motorista, tarifa…).
const CATALOG_LABELS: Record<string, string> = {};
for (const e of ENTITIES) for (const f of e.fields) CATALOG_LABELS[f.name] ??= f.label;

// 2) Colunas agregadas/derivadas das views de insight que o catálogo não cobre.
const FIELD_LABELS: Record<string, string> = {
  ...CATALOG_LABELS,
  competencia: "Competência",
  regiao: "Região",
  regiao_meta: "Região",
  status: "Status",
  n: "Total",
  count: "Total",
  n_cargas: "Cargas",
  n_viagens: "Viagens",
  viagens: "Viagens",
  peso_total: "Peso total (t)",
  frete_total: "Frete total",
  frete_medio: "Frete médio",
  cavalo: "Cavalo (placa)",
  ultima_competencia: "Última competência",
  tipo: "Tipo",
  nome: "Nome",
  validade: "Validade",
  dias_faltantes: "Dias restantes",
  sem_frete: "Sem frete",
  pct_sem_frete: "% sem frete",
  acumulado_pct: "% acumulado",
  kind: "Tipo",
  severity: "Severidade",
  sheet: "Aba",
  location: "Local",
  detail: "Detalhe",
  // Metas (alvo × realizado)
  meta_cargas: "Meta (cargas)",
  realizado: "Realizado",
  carregado: "Carregado",
  liberado: "Liberado",
  agendado: "Agendado",
  total_cargas: "Total",
  pct_atendida: "% atendida",
  pct_ritmo: "% do ritmo",
  veiculos_necessarios: "Veículos necessários",
  cargas_restantes: "Cargas restantes",
};

// Siglas que devem permanecer em maiúsculas ao gerar título a partir do nome.
const ACRONYMS = new Set(["pbtc", "cpf", "mopp", "cl", "vega", "usiminas", "id"]);

/**
 * Converte um nome técnico em rótulo legível. Usa o dicionário quando há
 * tradução curada; senão deriva de snake_case/camelCase com tratamento de
 * abreviações comuns (`pct`→%, `n`→nº) e siglas.
 */
export function humanizeField(key: string): string {
  if (!key) return "";
  const k = key.toLowerCase();
  if (FIELD_LABELS[k]) return FIELD_LABELS[k];
  return k
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → espaços
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) =>
      w === "pct" ? "%" : w === "n" ? "nº" : ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ")
    .trim();
}

// ---------------------------------------------------------------------
// Papéis de acesso (codes em types.ROLES) → nomes legíveis pt-BR.
// ---------------------------------------------------------------------
const ROLE_LABELS: Record<(typeof ROLES)[number], string> = {
  admin: "Administrador",
  torre: "Torre de controle",
  frota: "Frota",
  comercial: "Comercial",
  gestor: "Gestor",
  motorista: "Motorista",
};
export const roleLabel = (r: string): string =>
  ROLE_LABELS[r as (typeof ROLES)[number]] ?? humanizeField(String(r ?? ""));

// ---------------------------------------------------------------------
// Inconsistências: "kind" e "severity" são códigos técnicos no banco.
// ---------------------------------------------------------------------
// Catálogo de tipos de inconsistência: além do rótulo, uma explicação em
// português de (a) o que é, (b) como é detectado e (c) o que fazer. Alimenta o
// painel "Como funciona" da Central de inconsistências (fonte única).
// `nature`: 'auto' = achado automaticamente ao importar a planilha;
//           'curado' = registrado pela análise (limitação de migração conhecida).
export interface InconsistencyKindMeta {
  label: string;
  description: string;
  detection: string;
  action: string;
  nature: "auto" | "curado";
}

export const INCONSISTENCY_KIND_META: Record<string, InconsistencyKindMeta> = {
  formula_error: {
    label: "Erro de fórmula",
    description: "Células que a planilha original já exibia com erro (#N/A, #DIV/0!, #VALUE!, #REF!).",
    detection: "Ao importar, cada célula é lida; valores de erro do Excel/Sheets são contados por aba.",
    action: "Corrigir a origem do erro na planilha (lookup sem correspondência, divisão por zero) e reimportar.",
    nature: "auto",
  },
  duplicate_key: {
    label: "Chave duplicada",
    description: "A coluna-chave de uma aba tem valores repetidos, o que quebra buscas (VLOOKUP) e contagens.",
    detection: "Para abas com chave conhecida, os valores da coluna-chave são agrupados e os repetidos, listados.",
    action: "Unificar/renomear os registros duplicados (ex.: 'ARCELORMITTAL S.A.' vs 'ARCELORMITTAL S A').",
    nature: "auto",
  },
  empty_header: {
    label: "Cabeçalho vazio",
    description: "Colunas de dados sem título no cabeçalho — dificultam entender e mapear os campos.",
    detection: "A linha de cabeçalho de cada aba é verificada; colunas com dados mas sem nome são sinalizadas.",
    action: "Nomear a coluna na planilha de origem.",
    nature: "auto",
  },
  broken_ref: {
    label: "Referência quebrada",
    description: "Fórmula que aponta para uma célula/aba que não existe mais (#REF!).",
    detection: "Detectado junto com os erros de fórmula durante o parsing da planilha.",
    action: "Recriar a referência correta na planilha e reimportar.",
    nature: "auto",
  },
  google_only_function: {
    label: "Função só do Google Sheets",
    description: "Fórmulas exclusivas do Google Sheets (FILTER/ARRAYFORMULA) que o Excel exporta como DUMMYFUNCTION.",
    detection: "O parser reconhece essas funções e marca as abas onde precisam ser reimplementadas.",
    action: "Reimplementar a lógica no portal (o cálculo passa a ser feito pelo sistema, não pela planilha).",
    nature: "auto",
  },
  format_drift: {
    label: "Formato inconsistente",
    description: "O mesmo campo aparece em formatos diferentes (ex.: vírgula vs. ponto decimal) entre abas.",
    detection: "Registrado pela análise ao normalizar os dados na importação (curadoria).",
    action: "O sistema normaliza automaticamente; o aviso documenta a diferença de origem.",
    nature: "curado",
  },
  layout_variance: {
    label: "Layout divergente",
    description: "Cada cliente/mês organiza as colunas de forma diferente; a camada tipada uniformiza isso.",
    detection: "Registrado pela análise ao mapear os layouts VEGA/USIMINAS/CL (curadoria).",
    action: "Nenhuma ação do usuário — informativo. A camada 'raw' preserva tudo; a view tipada padroniza.",
    nature: "curado",
  },
};
export const kindMeta = (k: string): InconsistencyKindMeta =>
  INCONSISTENCY_KIND_META[k] ?? {
    label: humanizeField(String(k ?? "")),
    description: "Inconsistência de qualidade de dados.",
    detection: "—",
    action: "—",
    nature: "auto",
  };
export const kindLabel = (k: string): string => kindMeta(k).label;

export type SeverityTone = "danger" | "warning" | "info" | "neutral";
export interface SeverityMeta {
  label: string;
  tone: SeverityTone;
  icon: string;
  description: string;
}
const SEVERITY: Record<string, SeverityMeta> = {
  error: { label: "Erro", tone: "danger", icon: "⛔", description: "Precisa de correção — pode distorcer os números." },
  critical: { label: "Crítico", tone: "danger", icon: "⛔", description: "Impacto alto — priorizar a correção." },
  warning: { label: "Aviso", tone: "warning", icon: "⚠", description: "Merece atenção, mas não bloqueia a operação." },
  info: { label: "Informativo", tone: "info", icon: "ℹ", description: "Apenas contexto ou limitação conhecida." },
};
export const severityMeta = (s: string): SeverityMeta =>
  SEVERITY[String(s ?? "").toLowerCase()] ?? { label: humanizeField(String(s ?? "")), tone: "neutral", icon: "•", description: "" };

// ---------------------------------------------------------------------
// Detalhe da inconsistência: hoje é um JSON cru. Renderiza por forma conhecida.
// ---------------------------------------------------------------------
function parseDetail(detail: unknown): Record<string, unknown> | null {
  if (detail == null) return null;
  if (typeof detail === "object") return detail as Record<string, unknown>;
  if (typeof detail === "string") {
    try {
      const p = JSON.parse(detail);
      return p && typeof p === "object" ? (p as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
}

const fmtSample = (s: unknown): string => {
  if (s && typeof s === "object" && "row" in (s as Record<string, unknown>)) {
    const o = s as { row?: unknown; col?: unknown; err?: unknown };
    return `lin ${o.row}/col ${o.col}${o.err ? ` (${o.err})` : ""}`;
  }
  return String(s);
};

/** Transforma o JSON de detalhe em frase legível conforme o tipo de problema. */
export function inconsistencyDetail(_kind: string, detail: unknown): string {
  const d = parseDetail(detail);
  if (!d) return detail ? String(detail) : "—";

  if (typeof d.note === "string") return d.note;

  if (typeof d.count === "number") {
    const ex = Array.isArray(d.samples) ? d.samples.slice(0, 3).map(fmtSample).join("; ") : "";
    return `${d.count} célula(s) com erro${ex ? ` — ex.: ${ex}` : ""}`;
  }
  if (Array.isArray(d.examples)) {
    const head = d.examples.slice(0, 5).map(String).join(", ");
    return `${d.examples.length} chave(s) duplicada(s): ${head}${d.examples.length > 5 ? "…" : ""}`;
  }
  if (Array.isArray(d.targets)) return `Aponta para: ${d.targets.map(String).join(", ")}`;

  const entries = Object.entries(d);
  if (entries.length === 0) return "—";
  return entries.map(([k, v]) => `${humanizeField(k)}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ");
}
